import React, { useState } from 'react';
import { Play, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useSite } from '@/context/SiteContext';

const VideoGallery = () => {
  const { videos } = useSite();
  const [selectedVideo, setSelectedVideo] = useState(null);

  if (videos.length === 0) {
    return (
      <div className="text-center py-16">
        <p className="text-muted-foreground">No videos available yet.</p>
      </div>
    );
  }

  // Extract YouTube video ID from URL
  const getYouTubeId = (url) => {
    if (!url) return null;
    const match = url.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/);
    return match ? match[1] : null;
  };

  // Get embed URL for YouTube/Vimeo
  const getEmbedUrl = (url) => {
    if (!url) return null;
    const youtubeId = getYouTubeId(url);
    if (youtubeId) {
      return `https://www.youtube.com/embed/${youtubeId}?autoplay=1`;
    }
    // Handle Vimeo
    const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
    if (vimeoMatch) {
      return `https://player.vimeo.com/video/${vimeoMatch[1]}?autoplay=1`;
    }
    return null;
  };

  // Check if video has an uploaded file or external URL
  const hasUploadedFile = (video) => !!video.video_file;
  const hasExternalUrl = (video) => !!video.video_url;

  return (
    <>
      <div className="space-y-6">
        {videos.map((video) => (
          <div 
            key={video.id}
            className="group relative rounded-2xl overflow-hidden bg-card border border-border cursor-pointer hover:border-muted-foreground/30 transition-all duration-300"
            onClick={() => setSelectedVideo(video)}
          >
            <div className="flex flex-col md:flex-row">
              {/* Thumbnail */}
              <div className="md:w-1/2 lg:w-2/5 flex-shrink-0">
                <div className="aspect-video relative bg-muted">
                  {video.thumbnail ? (
                    <img 
                      src={video.thumbnail} 
                      alt={video.title}
                      className="w-full h-full object-cover"
                    />
                  ) : hasUploadedFile(video) ? (
                    <video 
                      src={video.video_file.replace('/api/uploads/', '/api/stream/')} 
                      className="w-full h-full object-cover"
                      muted
                      playsInline
                      preload="metadata"
                      onLoadedData={(e) => {
                        // Seek to first frame to show thumbnail
                        e.target.currentTime = 0.1;
                      }}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-muted to-muted-foreground/20">
                      <Play className="h-16 w-16 text-muted-foreground/50" />
                    </div>
                  )}
                  
                  {/* Play overlay */}
                  <div className="absolute inset-0 bg-background/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="w-16 h-16 rounded-full bg-foreground text-background flex items-center justify-center">
                      <Play className="h-7 w-7 ml-1" />
                    </div>
                  </div>

                  {/* Duration badge */}
                  {video.duration && (
                    <div className="absolute bottom-3 right-3 px-2 py-1 rounded bg-background/80 backdrop-blur-sm">
                      <span className="text-xs font-medium">{video.duration}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Info - to the right on desktop, below on mobile */}
              <div className="p-5 md:p-6 flex flex-col justify-center">
                <h3 className="font-serif text-xl md:text-2xl mb-3">{video.title}</h3>
                {video.description && (
                  <p className="text-sm md:text-base text-muted-foreground leading-relaxed">{video.description}</p>
                )}
                <div className="mt-4">
                  <span className="inline-flex items-center gap-2 text-sm text-muted-foreground group-hover:text-foreground transition-colors">
                    <Play className="h-4 w-4" />
                    Watch Video
                  </span>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Video Player Modal */}
      <Dialog open={!!selectedVideo} onOpenChange={() => setSelectedVideo(null)}>
        <DialogContent className="max-w-4xl p-0 bg-card border-border overflow-hidden">
          <DialogHeader className="p-4 pb-0">
            <DialogTitle className="font-serif text-xl">{selectedVideo?.title}</DialogTitle>
          </DialogHeader>
          
          <div className="aspect-video bg-background">
            {selectedVideo && hasUploadedFile(selectedVideo) ? (
              // Play uploaded video file - use stream endpoint for seeking support
              <video
                src={selectedVideo.video_file.replace('/api/uploads/', '/api/stream/')}
                className="w-full h-full"
                controls
                autoPlay
              />
            ) : selectedVideo && hasExternalUrl(selectedVideo) && getEmbedUrl(selectedVideo.video_url) ? (
              // Embed YouTube/Vimeo
              <iframe
                src={getEmbedUrl(selectedVideo.video_url)}
                className="w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                title={selectedVideo.title}
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <p className="text-muted-foreground">Video not available</p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default VideoGallery;
